import React from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { Check, ArrowRight, Zap, Shield, Users, Crown, Clock, Percent } from "lucide-react";
import Layout from "../components/layout/Layout";
import { useSubscription } from "../context/SubscriptionContext";

export default function PricingPage() {
  const { platformSettings } = useSubscription();

  const forExperts = {
    title: "Для экспертов",
    subtitle: "Начните консультировать без затрат",
    features: [
      {
        icon: Crown,
        title: "Премиум-подписка бесплатно",
        description: `Первые ${platformSettings.expertTrialDays} дней после регистрации вы получаете полный доступ ко всем функциям платформы`
      },
      {
        icon: Percent,
        title: `Комиссия ${platformSettings.commissionPercent}%`,
        description: "Комиссия удерживается из стоимости вашей консультации. Клиент платит вашу ставку, вы получаете остаток"
      },
      {
        icon: Clock,
        title: "Гибкое расписание",
        description: "Вы сами выбираете дни, часы и количество консультаций. Полный контроль над графиком"
      }
    ],
    included: [
      "Размещение в каталоге экспертов",
      "Личный кабинет с аналитикой",
      "Система бронирования и расписание",
      "Встроенный мессенджер с клиентами",
      "Отзывы и рейтинг",
      "Безлимитные консультации"
    ]
  };

  const forClients = {
    title: "Для клиентов",
    subtitle: "Выберите эксперта и запишитесь",
    features: [
      {
        icon: Users,
        title: "Доступ к каталогу",
        description: "Просматривайте профили экспертов, их опыт, отзывы и доступные слоты"
      },
      {
        icon: Shield,
        title: "Прозрачные цены",
        description: "Вы платите ровно ту сумму, которую указал эксперт. Без скрытых наценок"
      },
      {
        icon: Zap,
        title: "Мгновенное бронирование",
        description: "Выберите удобное время и забронируйте консультацию за пару кликов"
      }
    ]
  };

  const example = {
    expertRate: 15000,
    commission: platformSettings.commissionPercent
  };
  const platformFee = Math.round(example.expertRate * (example.commission / 100));
  const expertEarnings = example.expertRate - platformFee;

  return (
    <Layout>
      <div id="pricing_page">
        <section className="py-16 lg:py-24 bg-gradient-to-b from-primary-950/30 to-transparent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-4xl lg:text-5xl font-medium text-white mb-6">
                Как устроены тарифы
              </h1>
              <p className="text-xl font-extralight text-neutral-300 leading-relaxed">
                Простая и прозрачная модель. Клиент платит цену эксперта, платформа берёт комиссию из этой суммы
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-8 rounded-md border border-primary-600/50 bg-primary-950/20"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Crown className="w-7 h-7 text-primary-400" />
                  <h2 className="text-2xl font-medium text-white">{forExperts.title}</h2>
                </div>
                <p className="text-lg font-extralight text-neutral-400 mb-8">
                  {forExperts.subtitle}
                </p>

                <div className="space-y-6 mb-8">
                  {forExperts.features.map((f, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-10 h-10 rounded-md bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                        <f.icon className="w-5 h-5 text-primary-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-white mb-1">{f.title}</h3>
                        <p className="text-lg font-extralight text-neutral-400">{f.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-neutral-800 pt-6">
                  <h4 className="text-lg font-medium text-white mb-4">
                    Включено в премиум:
                  </h4>
                  <ul className="space-y-3">
                    {forExperts.included.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5" />
                        <span className="text-lg font-extralight text-neutral-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="p-8 rounded-md border border-neutral-800 bg-neutral-900/50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-7 h-7 text-accent-400" />
                  <h2 className="text-2xl font-medium text-white">{forClients.title}</h2>
                </div>
                <p className="text-lg font-extralight text-neutral-400 mb-8">
                  {forClients.subtitle}
                </p>

                <div className="space-y-6 mb-8">
                  {forClients.features.map((f, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-10 h-10 rounded-md bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                        <f.icon className="w-5 h-5 text-accent-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-white mb-1">{f.title}</h3>
                        <p className="text-lg font-extralight text-neutral-400">{f.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-neutral-800 pt-6">
                  <h4 className="text-lg font-medium text-white mb-4">
                    Пример расчёта
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between p-4 rounded-md bg-neutral-800/50">
                      <span className="text-lg font-extralight text-neutral-400">Ставка эксперта</span>
                      <span className="text-lg font-medium text-white">
                        {example.expertRate.toLocaleString("ru-RU")} ₽/час
                      </span>
                    </div>
                    <div className="flex justify-between p-4 rounded-md bg-neutral-800/50">
                      <span className="text-lg font-extralight text-neutral-400">Вы платите</span>
                      <span className="text-lg font-medium text-white">
                        {example.expertRate.toLocaleString("ru-RU")} ₽
                      </span>
                    </div>
                    <div className="flex justify-between p-4 rounded-md bg-neutral-800/30">
                      <span className="text-lg font-extralight text-neutral-500">Из них комиссия платформы ({example.commission}%)</span>
                      <span className="text-lg font-extralight text-neutral-500">
                        {platformFee.toLocaleString("ru-RU")} ₽
                      </span>
                    </div>
                    <div className="flex justify-between p-4 rounded-md bg-neutral-800/30">
                      <span className="text-lg font-extralight text-neutral-500">Эксперт получает</span>
                      <span className="text-lg font-extralight text-neutral-500">
                        {expertEarnings.toLocaleString("ru-RU")} ₽
                      </span>
                    </div>
                  </div>
                </div>

                <Link
                  to="/experts"
                  className="mt-8 w-full flex items-center justify-center gap-2 py-4 rounded-md bg-neutral-800 hover:bg-neutral-700 text-white text-lg font-medium transition-colors"
                >
                  Найти эксперта
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20 bg-neutral-900/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Shield,
                  title: "Безопасные платежи",
                  description: "Все транзакции защищены и проходят через безопасные каналы"
                },
                {
                  icon: Zap,
                  title: "Мгновенный доступ",
                  description: "После регистрации все функции доступны сразу, без ожидания"
                },
                {
                  icon: Users,
                  title: "Без скрытых платежей",
                  description: "Клиент всегда видит итоговую сумму. Эксперт знает свой заработок"
                }
              ].map((b, index) => (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-4 rounded-md bg-primary-800/20 flex items-center justify-center">
                    <b.icon className="w-7 h-7 text-primary-400" />
                  </div>
                  <h3 className="text-xl font-medium text-white mb-2">
                    {b.title}
                  </h3>
                  <p className="text-lg font-extralight text-neutral-400">
                    {b.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}